import {Box, Paper, Typography, useTheme} from "@mui/material";
import {PieChart} from "@mui/icons-material";
import {formatCurrency, formatNumber, formatPercent} from "../utils/formatters";

const QUADRANTS = [
    {key: 'grow', label: 'Grow', color: '#42A5F5', fg: '#fff'},
    {key: 'defend', label: 'Defend', color: '#2E7D32', fg: '#fff'},
    {key: 'review_and_fix', label: 'Review/Fix', color: '#E53935', fg: '#fff'},
    {key: 'optimize', label: 'Optimize', color: '#FB8C00', fg: '#212121'}
];

const VIEW_ENTITY_LABELS = {
    customers: 'Customers',
    products: 'SKUs',
    sales_reps: 'Sales Reps',
};

export const SegmentationMatrixCard = ({
    matrix,
    breakdownView,
    selectedSegment,
    onSelectSegment,
    cardHeight = null,
    matrixWidth = 360,
}) => {
    const theme = useTheme();
    const axisFont = 14;
    const entityLabel = VIEW_ENTITY_LABELS[breakdownView] || 'Entities';

    const renderQuadrantCell = ({key, label, color, fg}) => {
        const q = matrix?.segments?.[key] || {
            net_revenue: 0,
            variable_margin: 0,
            variable_margin_percent: 0,
            entityCount: 0,
        };
        const selected = selectedSegment === key;
        return (
            <Box
                key={key}
                onClick={() => onSelectSegment?.(selected ? null : key)}
                sx={{
                    background: color,
                    color: fg,
                    borderRadius: 1.5,
                    p: 1.25,
                    cursor: 'pointer',
                    border: selected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                    boxShadow: selected ? '0 0 0 2px rgba(255,255,255,0.85) inset' : 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    width: '100%',
                    aspectRatio: '1 / 1',
                    height: 'auto',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    minHeight: 0
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{fontWeight: 700, lineHeight: 1.15, color: 'inherit', fontSize: '1.05rem', alignSelf: 'flex-start'}}
                >
                    {label}
                </Typography>
                <Box sx={{flex: 1, minHeight: 4}}/>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35, alignSelf: 'flex-start', width: '100%'}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 800, lineHeight: 1.1, color: 'inherit', fontSize: '1.15rem'}}>
                        {formatCurrency(q.net_revenue, true)}
                    </Typography>
                    <Typography variant="body2" sx={{opacity: 0.95, lineHeight: 1.2, color: 'inherit', fontSize: '0.9rem'}}>
                        VM {formatCurrency(q.variable_margin, true)} · {formatPercent(q.variable_margin_percent, 0)}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 700, color: 'inherit', fontSize: '0.9rem', lineHeight: 1.2}}>
                        {q.entityCount} {entityLabel}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const paperStyle = {
        ...theme.palette.contentPanel,
        padding: 12,
        height: cardHeight ?? '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        minHeight: 0
    };

    return (
        <Paper style={paperStyle} elevation={0}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.25}}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    background: theme.palette.button.lightblue,
                    color: theme.palette.primary.main,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    '& .MuiSvgIcon-root': {fontSize: 22}
                }}>
                    <PieChart/>
                </Box>
                <Typography variant="subtitle1" sx={{fontWeight: 700, color: theme.palette.primary.main}}>
                    Segmentation
                </Typography>
            </Box>

            {matrix && (
                <Typography variant="body2" color="textSecondary" sx={{mb: 1, fontSize: '0.875rem'}}>
                    Benchmark VM%: {formatPercent(matrix.averageVMPercent, 2)}
                    {' · '}
                    Avg volume: {formatNumber(matrix.averageVolume) ?? '0'}
                </Typography>
            )}

            <Box sx={{display: 'flex', flex: 1, minHeight: 0, width: matrixWidth, maxWidth: '100%', mx: 'auto'}}>
                <Box sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: axisFont,
                    color: 'text.secondary',
                    mr: 0.75,
                    flexShrink: 0,
                    lineHeight: 1.2
                }}>
                    VM% (low → high)
                </Box>
                <Box sx={{flex: 1, minWidth: 0, width: 0}}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1
                    }}>
                        {QUADRANTS.map(renderQuadrantCell)}
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{display: 'block', textAlign: 'center', mt: 0.75, fontSize: `${axisFont}px`}}>
                        Volume vs average (low → high)
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};
